/**
 * Planned Work Detail View
 * Comprehensive view for managing individual planned works
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Tab,
  Tabs,
  Paper,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Stack,
  Breadcrumbs,
  Link,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as StartIcon,
  Stop as CompleteIcon,
  Assignment as PermitIcon,
  Group as TeamIcon,
  Build as ToolsIcon,
  Security as SafetyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { format } from 'date-fns';

import { useSiteContext } from '../../hooks/useSiteContext';
import plannedWorksService from '../../services/plannedWorksService';
import { WorkTodoList } from '../../components/PlannedWorks/WorkTodoList';
import { WorkProgressTracker } from '../../components/PlannedWorks/WorkProgressTracker';
import WorkProgressManager from '../../components/PlannedWorks/WorkProgressManager';
import {
  PlannedWorkDetail as PlannedWorkDetailType,
  WorkApproval,
  getWorkStatusColor,
  getPriorityColor,
  getApprovalStatusColor
} from '../../types/plannedWorks';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`work-detail-tabpanel-${index}`}
      aria-labelledby={`work-detail-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const PlannedWorkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentSite } = useSiteContext();
  
  // State
  const [work, setWork] = useState<PlannedWorkDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Computed properties
  const canEdit = work?.status === 'in_progress' || work?.status === 'draft' || work?.status === 'proposed';
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [permitDialogOpen, setPermitDialogOpen] = useState(false);

  // Fetch work details
  const fetchWorkDetail = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const workDetail = await plannedWorksService.plannedWorks.getById(parseInt(id));
      setWork(workDetail);
    } catch (err) {
      console.error('Failed to fetch work detail:', err);
      setError('Failed to load work details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkDetail();
  }, [id]);

  const handleWorkUpdate = (updatedWork: PlannedWorkDetailType) => {
    setWork(updatedWork);
  };

  const handleDelete = async () => {
    if (!work) return;
    
    try {
      await plannedWorksService.plannedWorks.delete(work.id);
      navigate('/planned-works');
    } catch (err) {
      console.error('Failed to delete work:', err);
      setError('Failed to delete work');
    }
  };

  const handleGeneratePermit = async () => {
    if (!work) return;
    
    try {
      await plannedWorksService.permitIntegration.generatePermit(work.id);
      setPermitDialogOpen(false);
      fetchWorkDetail(); // Refresh to show generated permit
    } catch (err) {
      console.error('Failed to generate permit:', err);
      setError('Failed to generate permit');
    }
  };

  const renderWorkInfo = () => {
    if (!work) return null;

    return (
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Work Information
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Work Number
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.work_number}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Work Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.work_type_name} ({work.work_type_category})
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.description}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Proposed By
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.proposed_by_name}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Assigned To
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.assigned_to_name || 'Unassigned'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Location
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.location_name || 'Not specified'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Estimated Duration
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {work.estimated_duration_hours} hours
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Status and Progress */}
        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* Status Card */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Status
                </Typography>
                
                <Stack spacing={2}>
                  <Box>
                    <Chip
                      label={work.status_display}
                      color={getWorkStatusColor(work.status)}
                      size="medium"
                    />
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Priority
                    </Typography>
                    <Chip
                      label={work.priority_display}
                      color={getPriorityColor(work.priority)}
                      variant="outlined"
                    />
                  </Box>
                  
                  {work.current_approval_stage && (
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        Current Approval Stage
                      </Typography>
                      <Typography variant="body2">
                        {work.current_approval_stage.replace('_', ' ')}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Dates Card */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Schedule
                </Typography>
                
                <List dense>
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <ScheduleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Proposed Start"
                      secondary={format(new Date(work.proposed_start_date), 'MMM dd, yyyy')}
                    />
                  </ListItem>
                  
                  <ListItem sx={{ px: 0 }}>
                    <ListItemIcon>
                      <ScheduleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Proposed End"
                      secondary={format(new Date(work.proposed_end_date), 'MMM dd, yyyy')}
                    />
                  </ListItem>
                  
                  {work.actual_start_date && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <ScheduleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Actual Start"
                        secondary={format(new Date(work.actual_start_date), 'MMM dd, yyyy')}
                      />
                    </ListItem>
                  )}
                  
                  {work.actual_end_date && (
                    <ListItem sx={{ px: 0 }}>
                      <ListItemIcon>
                        <ScheduleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Actual End"
                        secondary={format(new Date(work.actual_end_date), 'MMM dd, yyyy')}
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    );
  };

  const renderRequirements = () => {
    if (!work) return null;

    return (
      <Grid container spacing={3}>
        {/* Work Requirements */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Work Requirements
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {work.permit_required && (
                    <Chip icon={<PermitIcon />} label="Permit Required" color="warning" variant="outlined" />
                  )}
                  {work.isolation_required && (
                    <Chip icon={<SafetyIcon />} label="Isolation Required" color="error" variant="outlined" />
                  )}
                  {work.confined_space_entry && (
                    <Chip icon={<SafetyIcon />} label="Confined Space" color="error" variant="outlined" />
                  )}
                  {work.hot_work && (
                    <Chip icon={<SafetyIcon />} label="Hot Work" color="error" variant="outlined" />
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Required Crew Size
                  </Typography>
                  <Typography variant="body1">
                    {work.required_crew_size} person(s)
                  </Typography>
                </Box>

                {work.required_skills && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Required Skills
                    </Typography>
                    <Typography variant="body2">
                      {work.required_skills}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Resources */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resources
              </Typography>
              
              <Stack spacing={2}>
                {work.required_tools && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Required Tools
                    </Typography>
                    <Typography variant="body2">
                      {work.required_tools}
                    </Typography>
                  </Box>
                )}

                {work.required_parts && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Required Parts
                    </Typography>
                    <Typography variant="body2">
                      {work.required_parts}
                    </Typography>
                  </Box>
                )}

                {work.safety_requirements && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Safety Requirements
                    </Typography>
                    <Typography variant="body2">
                      {work.safety_requirements}
                    </Typography>
                  </Box>
                )}

                {work.special_instructions && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Special Instructions
                    </Typography>
                    <Typography variant="body2">
                      {work.special_instructions}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Equipment */}
        {work.equipment_details.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Equipment Involved
                </Typography>
                
                <List>
                  {work.equipment_details.map((equipment, index) => (
                    <ListItem key={equipment.id} divider={index < work.equipment_details.length - 1}>
                      <ListItemText
                        primary={equipment.name}
                        secondary={
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            <Chip label={equipment.equipment_id} size="small" variant="outlined" />
                            {equipment.kks_code && (
                              <Chip label={equipment.kks_code} size="small" variant="outlined" />
                            )}
                            <Chip 
                              label={equipment.status} 
                              size="small" 
                              color={equipment.status === 'in_service' ? 'success' : 'warning'}
                            />
                            <Chip 
                              label={equipment.criticality} 
                              size="small" 
                              color={equipment.criticality === 'critical' ? 'error' : 'default'}
                            />
                          </Stack>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderApprovals = () => {
    if (!work || !work.approvals.length) {
      return (
        <Alert severity="info">
          No approvals have been created for this work yet.
        </Alert>
      );
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Approval History
          </Typography>
          
          <List>
            {work.approvals.map((approval, index) => (
              <ListItem key={approval.id} divider={index < work.approvals.length - 1}>
                <ListItemText
                  primary={approval.approval_stage_display}
                  secondary={
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={approval.status_display}
                          color={getApprovalStatusColor(approval.status)}
                          size="small"
                        />
                        <Typography variant="body2" color="textSecondary">
                          by {approval.approver_name}
                        </Typography>
                        {approval.approved_at && (
                          <Typography variant="body2" color="textSecondary">
                            on {format(new Date(approval.approved_at), 'MMM dd, yyyy')}
                          </Typography>
                        )}
                      </Box>
                      
                      {approval.comments && (
                        <Typography variant="body2">
                          {approval.comments}
                        </Typography>
                      )}
                      
                      {approval.conditions_applied && (
                        <Alert severity="warning" sx={{ py: 0 }}>
                          <Typography variant="body2">
                            Conditions: {approval.conditions_applied}
                          </Typography>
                        </Alert>
                      )}
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !work) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error || 'Work not found'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body1"
            onClick={() => navigate('/planned-works')}
            sx={{ textDecoration: 'none', color: 'primary.main' }}
          >
            Planned Works
          </Link>
          <Typography color="text.primary">{work.work_number}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              {work.title}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {work.work_number} • {work.work_type_name}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchWorkDetail}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/planned-works')}
            >
              Back
            </Button>
            
            {work.permit_required && !work.generated_permit && (
              <Button
                variant="outlined"
                startIcon={<PermitIcon />}
                onClick={() => setPermitDialogOpen(true)}
              >
                Generate Permit
              </Button>
            )}
            
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => {/* Navigate to edit */}}
            >
              Edit
            </Button>
            
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </Stack>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Progress Tracker */}
      <Box sx={{ mb: 3 }}>
        <WorkProgressTracker
          plannedWork={work}
          onWorkUpdate={handleWorkUpdate}
          showActions={true}
        />
      </Box>

      {/* Main Content Tabs */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Work Information" />
            <Tab label="Requirements" />
            <Tab label="Tasks" />
            <Tab label="Progress" />
            <Tab label="Approvals" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {renderWorkInfo()}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderRequirements()}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <WorkTodoList
            plannedWork={work}
            onTodoUpdate={(updatedTodo) => {
              // Update todo in the work object
              const updatedWork = {
                ...work,
                todo_items: work.todo_items.map(item =>
                  item.id === updatedTodo.id ? updatedTodo : item
                )
              };
              setWork(updatedWork);
            }}
            onWorkUpdate={handleWorkUpdate}
            editable={work.status === 'in_progress'}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <WorkProgressManager
            work={work}
            onWorkUpdated={setWork}
            readOnly={!canEdit}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          {renderApprovals()}
        </TabPanel>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Planned Work</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this planned work? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Permit Dialog */}
      <Dialog open={permitDialogOpen} onClose={() => setPermitDialogOpen(false)}>
        <DialogTitle>Generate Permit</DialogTitle>
        <DialogContent>
          <Typography>
            Generate a permit for this planned work? The permit will be created with the work details and requirements.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermitDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleGeneratePermit} variant="contained">
            Generate Permit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
